import { Box, Paper, Typography, IconButton, TextField, Avatar } from '@mui/material';
import { useState } from 'react';
import { Comment } from '../../../../services/ticketEasyService/model/ticketEasy/Comment';
import { useCommentDeleteMutation, useCommentUpdateMutation } from '../../../../services/ticketEasyService/ticketEasy-api';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { ticketEasyColors } from '../../TicketEasyTheme';

interface CommentItemProps {
    comment: Comment;
    ticketId: string;
    isCommentOwner: boolean;
}

export function CommentItem({ comment, ticketId, isCommentOwner }: CommentItemProps) {
    const [deleteComment] = useCommentDeleteMutation();
    const [updateComment] = useCommentUpdateMutation();
    
    const [editingComment, setEditingComment] = useState(false);
    const [editedCommentText, setEditedCommentText] = useState(comment.comment);
    
    const getInitial = (name: string) => {
        return name ? name.charAt(0).toUpperCase() : '?';
    };

    const handleCommentEdit = (e?: React.MouseEvent) => {
        if (e) e.preventDefault();
        setEditingComment(true);
        setEditedCommentText(comment.comment);
    };
    
    const handleCommentEditCancel = (e?: React.MouseEvent) => {
        if (e) e.preventDefault();
        setEditingComment(false);
        setEditedCommentText(comment.comment);
    };
    
    const handleCommentUpdate = async (e?: React.MouseEvent) => {
        if (e) e.preventDefault();
        try {
            await updateComment({
                ...comment,
                comment: editedCommentText
            }).unwrap();
            setEditingComment(false);
        } catch (error) {
            console.error("Failed to update comment:", error);
        }
    };
    
    const handleCommentDelete = async (e?: React.MouseEvent) => {
        if (e) e.preventDefault();
        if (window.confirm("Are you sure you want to delete this comment?")) {
            try {
                await deleteComment({
                    commentId: comment.id,
                    ticketId: ticketId
                }).unwrap();
            } catch (error) {
                console.error("Failed to delete comment:", error);
            }
        }
    };
    
    if (comment.isDeleted) return null;
    return (
        <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Avatar 
                    sx={{ 
                        bgcolor: ticketEasyColors.psnGreen, 
                        color: 'white',
                        mr: 1,
                        width: 28,
                        height: 28,
                        fontSize: '0.85rem'
                    }}
                >
                    {getInitial(comment.user)}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                            {comment.user}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {comment.createdDate}
                            </Typography>
                            {comment.updatedDate && comment.updatedDate !== comment.createdDate && comment.updatedDate.trim() !== '' && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                                    Edited{` on ${comment.updatedDate}`}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    
                    {editingComment ? (
                        <Box>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                size="small"
                                value={editedCommentText}
                                onChange={(e) => setEditedCommentText(e.target.value)}
                                variant="outlined"
                                sx={{ mb: 0.5 }}
                                autoFocus
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <IconButton 
                                    size="small" 
                                    onClick={handleCommentEditCancel}
                                    sx={{ mr: 0.5, p: 0.5 }}
                                >
                                    <CancelIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={handleCommentUpdate}
                                    disabled={!editedCommentText.trim()}
                                    sx={{ p: 0.5 }}
                                >
                                    <SaveIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    ) : (
                        <Paper 
                            variant="outlined" 
                            sx={{ 
                                p: 1,
                                backgroundColor: '#f8f9fa',
                                borderRadius: '0.5rem',
                                position: 'relative',
                                minHeight: '1.5rem'
                            }}
                        >
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                                {comment.comment}
                            </Typography>
                            
                            {isCommentOwner && (
                                <Box sx={{ 
                                    position: 'absolute', 
                                    top: '2px',
                                    right: '2px',
                                    opacity: 0.6,
                                    '&:hover': { opacity: 1 }
                                }}>
                                    <IconButton 
                                        size="small" 
                                        onClick={handleCommentEdit}
                                        sx={{ mr: 0.25, p: 0.25 }}
                                    >
                                        <EditIcon sx={{ fontSize: '0.9rem' }} />
                                    </IconButton>
                                    <IconButton 
                                        size="small" 
                                        color="error"
                                        onClick={handleCommentDelete}
                                        sx={{ p: 0.25 }}
                                    >
                                        <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                                    </IconButton>
                                </Box>
                            )}
                        </Paper>
                    )}
                </Box>
            </Box>
        </Box>
    );
}